import { useEffect } from 'react';
import { Sentry } from '@/lib/sentry';
import { useAuth } from '@/hooks/useAuth';
import { useLocationStore } from '@/store/location';

/**
 * Syncs authenticated user identity and multi-tenant context into Sentry scope.
 * Call once at the top of RootLayout alongside other side-effect hooks.
 *
 * Sets user.id, user.email, and tags: user_role, org_id, org_slug, location_id, location_name.
 * Clears all identity from scope on sign-out.
 */
export function useSentryUser(): void {
  const { user, profile, organization } = useAuth();
  const currentLocation = useLocationStore((s) => s.currentLocation);

  // Sync user identity and role
  useEffect(() => {
    if (user && profile) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
      });
      Sentry.setTag('user_role', profile.role);
    } else if (!user) {
      Sentry.setUser(null);
      Sentry.setTag('user_role', null);
      Sentry.setTag('org_id', null);
      Sentry.setTag('org_slug', null);
      Sentry.setTag('location_id', null);
      Sentry.setTag('location_name', null);
    }
  }, [user, profile]);

  // Sync organization context
  useEffect(() => {
    if (organization) {
      Sentry.setTag('org_id', organization.id);
      Sentry.setTag('org_slug', organization.slug);
      Sentry.setContext('organization', {
        id: organization.id,
        name: organization.name,
        slug: organization.slug,
        business_type: organization.business_type ?? null,
      });
    }
  }, [organization]);

  // Sync active location
  useEffect(() => {
    if (currentLocation) {
      Sentry.setTag('location_id', currentLocation.id);
      Sentry.setTag('location_name', currentLocation.name);
    } else {
      Sentry.setTag('location_id', null);
      Sentry.setTag('location_name', null);
    }
  }, [currentLocation]);
}
